import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import SDK from '../../../../SDK';
import { IconAddCircle, IconChevronRight, IconSearchField } from '../../../../Common/Icons';
import logger from '../../../../../Helpers/logger';
import { useLanguage } from '../../../../../localization/useLanguage';
import { Dialog } from '../../../../../ui/Dialog';
import DialogFrame from '../../../../../ui/DialogFrame';
import DialogHead from '../../../../../ui/DialogFrame/DialogHead';
import DialogToaster from '../../../../../ui/DialogFrame/DialogToaster';
import { cn } from '../../../../../ui/lib/utils';
import ContactAvatar from '../ContactAvatar';
import { CONTACT_LOAD_THRESHOLD, SEARCH_DEBOUNCE_MS, SEARCH_MAX_LENGTH } from '../constants';
import { formatContactPhone, getContactName, groupContactsByLetter } from '../contactHelpers';
import dialogStyles from '../dialog.module.scss';
import useContactSearch from '../useContactSearch';
import styles from './index.module.scss';

// showModal() focuses the first control (the close icon); focus the search once it has run.
const SEARCH_FOCUS_DELAY_MS = 60;

const ContactRow = ({ contact, onSelect }) => (
    <button type="button" className={styles.row} onClick={() => onSelect(contact)}>
        <ContactAvatar contact={contact} />
        <span className={styles.rowText}>
            <b title={getContactName(contact)}>{getContactName(contact)}</b>
            <span>{formatContactPhone(contact)}</span>
        </span>
        <span className={styles.chevron}>
            <IconChevronRight />
        </span>
    </button>
);

ContactRow.propTypes = {
    contact: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
};

// The three most contacted accounts, fetched each time the pop-up opens.
const useFrequentContacts = open => {
    const [frequentContacts, setFrequentContacts] = useState([]);

    useEffect(() => {
        if (!open) return undefined;
        let isCancelled = false;
        SDK.getFrequentContacts().then(response => {
            if (isCancelled) return;
            if (response?.statusCode === 200) {
                setFrequentContacts(response.data || []);
            } else {
                logger.error('Frequent contacts failed', { statusCode: response?.statusCode });
            }
        });
        return () => {
            isCancelled = true;
        };
    }, [open]);

    return frequentContacts;
};

/**
 * Contact list pop-up: frequently contacted first, then A→Z, one contact picked per opening.
 * FR60 opens it to start a chat; FR119 opens it to share a contact card.
 *
 * @param {boolean} open Whether the pop-up is shown.
 * @param {Function} onClose Closes the pop-up.
 * @param {Function} onSelect Called with the clicked contact.
 * @param {Function} onAddContact Opens the New Contact pop-up; the row is hidden when absent.
 * @param {string} title Translated title; defaults to the FR60 "Contact List".
 * @param {string} noMatchText Translated note shown when a search finds nothing.
 * @param {number} searchDebounceMs Delay before the typed keyword is searched.
 * @param {boolean} isSearchResetOnOpen Clears and focuses the search each time the pop-up opens.
 */
const ContactListDialog = ({
    open,
    onClose,
    onSelect,
    onAddContact,
    title,
    noMatchText,
    searchDebounceMs = SEARCH_DEBOUNCE_MS,
    isSearchResetOnOpen = false,
}) => {
    const { t } = useLanguage();
    const search = useContactSearch(open, searchDebounceMs);
    const frequentContacts = useFrequentContacts(open);
    const searchInputRef = useRef(null);
    const { reset: resetSearch } = search;

    useEffect(() => {
        if (!open || !isSearchResetOnOpen) return undefined;
        resetSearch();
        const timer = setTimeout(() => searchInputRef.current?.focus(), SEARCH_FOCUS_DELAY_MS);
        return () => clearTimeout(timer);
    }, [open, isSearchResetOnOpen, resetSearch]);

    // The section hides as soon as typing starts, before the debounced search runs.
    const isFrequentShown = !search.inputValue.trim() && !search.keyword && frequentContacts.length > 0;

    const letterGroups = useMemo(() => {
        const frequentIds = isFrequentShown ? new Set(frequentContacts.map(contact => contact.userId)) : new Set();
        return groupContactsByLetter(search.contacts.filter(contact => !frequentIds.has(contact.userId)));
    }, [frequentContacts, isFrequentShown, search.contacts]);

    const isEmpty = search.hasLoaded && !search.isLoading && !isFrequentShown && letterGroups.length === 0;
    const emptyText = search.keyword && noMatchText ? noMatchText : t('chat.EMPTY_NO_DATA');

    const handleScroll = event => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight * CONTACT_LOAD_THRESHOLD) search.onLoadMore();
    };

    const handleKeyDown = event => {
        if (event.key === 'Enter') search.onSubmit();
    };

    return (
        <Dialog open={open} onOpenChange={isOpen => !isOpen && open && onClose()}>
            <DialogFrame>
                <DialogToaster open={open} />
                <DialogHead title={t('createChat.CONTACT_LIST_TITLE')} onClose={onClose} />

                <div className={dialogStyles.search}>
                    <span className={dialogStyles.searchIcon}>
                        <IconSearchField />
                    </span>
                    <input
                        ref={searchInputRef}
                        type="search"
                        value={search.inputValue}
                        maxLength={SEARCH_MAX_LENGTH}
                        placeholder={t('createChat.SEARCH_CONTACTS')}
                        aria-label={t('createChat.SEARCH_CONTACTS')}
                        onChange={event => search.onInputChange(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                {onAddContact && (
                    <button
                        type="button"
                        className={cn(dialogStyles.addContact, dialogStyles.addContactPlain)}
                        onClick={onAddContact}
                    >
                        <span className={dialogStyles.addContactBadge}>
                            <IconAddCircle />
                        </span>
                        {t('createChat.ADD_CONTACT')}
                    </button>
                )}

                <div className={styles.list} onScroll={handleScroll}>
                    {isFrequentShown && (
                        <>
                            <p className={dialogStyles.sectionCaption}>{t('createChat.FREQUENTLY_CONTACTED')}</p>
                            <div className={styles.card}>
                                {frequentContacts.map(contact => (
                                    <ContactRow key={contact.userId} contact={contact} onSelect={onSelect} />
                                ))}
                            </div>
                        </>
                    )}

                    {letterGroups.map(({ letter, contacts }) => (
                        <div key={letter}>
                            <p className={dialogStyles.sectionCaption}>{letter}</p>
                            <div className={styles.card}>
                                {contacts.map(contact => (
                                    <ContactRow key={contact.userId} contact={contact} onSelect={onSelect} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {isEmpty && <p className={dialogStyles.emptyNote}>{emptyText}</p>}
                </div>
            </DialogFrame>
        </Dialog>
    );
};

ContactListDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onAddContact: PropTypes.func,
    title: PropTypes.string,
    noMatchText: PropTypes.string,
    searchDebounceMs: PropTypes.number,
    isSearchResetOnOpen: PropTypes.bool,
};

export default ContactListDialog;
